import { ActionIcon, Tooltip } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import React, { useMemo } from 'react';
import { MantineColumn, MantineTable } from '../../../../../../react-commons/components/table/MantineTable';
import { useNestedDialogFunction } from '../../../../../../react-commons/dialog-provider/UseNestedDialogFunction';
import { QueryBaseComponent } from '../../../../../../react-commons/query/QueryBaseComponent';
import { ResourceType } from '../../../../resource/enumerations/ResourceType';
import { VCycleElementType } from '../../../../resource/enumerations/VCycleElementType';
import {
    getDefaultVCycleElementResource,
    useVCycleElementActions,
    VCycleElementParentRelation,
    VCycleElementPropertyValidation,
    VCycleElementResource,
} from '../../../../resource/resources/artifacts/use_case/representation/vcycleElementResource';
import { EditDeleteButtons } from '../../../reusable-components/base/EditDeleteButtons';
import { ControlledReferenceInputs } from '../../../reusable-components/base/reference-text-fields/ControlledReferenceInputs';
import { useDeleteResourceDialogFunction } from '../../../reusable-components/dialogs/UseDeleteResourceDialogFunction';
import { VCycleElementCreationDialog } from '../vcycle-element/VCycleElementCreationDialog';
import { VCycleElementEditDialog } from '../vcycle-element/VCycleElementEditDialog';

export const VCycleElementTable = ({
    parentId,
    projectId,
    projectVersionId,
}: {
    parentId?: string;
    projectId: string | undefined | null;
    projectVersionId: string | undefined | null;
}) => {
    const deleteResourceDialogFunction = useDeleteResourceDialogFunction();
    const nestedDialog = useNestedDialogFunction();

    const vcycleElementActions = useVCycleElementActions();
    const vcycleElementsQuery = vcycleElementActions.useGetAllResources(
        VCycleElementParentRelation.PROCESS_AREA_V_CYCLE_ELEMENT_CONTAINER,
        parentId
    );
    const deleteVCycleElement = vcycleElementActions.useDeleteResource();

    const openEditDialog = (resource: VCycleElementResource) => {
        nestedDialog.showNestedDialog(
            <VCycleElementEditDialog
                onClose={nestedDialog.closeNestedDialog}
                objectId={resource.objectId}
                projectId={projectId}
                versionedInId={resource.versionedIn}
            />
        );
    };

    const openAddDialog = () => {
        nestedDialog.showNestedDialog(
            <VCycleElementCreationDialog
                projectId={projectId}
                projectVersionId={projectVersionId}
                onClose={nestedDialog.closeNestedDialog}
                parentId={parentId ?? ''}
                autoGeneratedProps={{
                    ...getDefaultVCycleElementResource(VCycleElementType.PROCESS_AREA),
                }}
            />
        );
    };

    const columns = useMemo((): Array<MantineColumn<VCycleElementResource>> => {
        return [
            {
                Header: null,
                type: 'reference',
                accessorKey: 'processArea',
                header: 'Process Area',
                Cell: ({ row: { original } }) =>
                    'processArea' in original ?
                        <ControlledReferenceInputs.ProcessArea
                            objectId={original.processArea}
                            readOnly={true}
                            projectVersionId={projectVersionId}
                            projectId={projectId}
                            propertyValidationInformation={{
                                resourceType: ResourceType.V_CYCLE_ELEMENT,
                                propertyIdentifier: VCycleElementPropertyValidation.PROCESS_AREA,
                                resourceId: original.id,
                            }}
                            noLabel
                        />
                    :   undefined,
            },
        ];
    }, []);

    return (
        <QueryBaseComponent name="VCycleElements" query={vcycleElementsQuery}>
            {(data) => (
                <div
                    style={{
                        height: '100%',
                        minHeight: 100,
                        position: 'relative',
                        overflowY: 'auto',
                    }}
                >
                    <MantineTable
                        columns={columns}
                        data={data}
                        label={undefined}
                        enableActions={true}
                        initialPageSize={10}
                        fillParent={false}
                        rowActionsWidth={60}
                        rowActionsHeader={
                            <Tooltip label={'Add'}>
                                <ActionIcon
                                    variant={'transparent'}
                                    color={'var(--mantine-text-secondary-color)'}
                                    onClick={openAddDialog}
                                    size={'sm'}
                                >
                                    <IconPlus />
                                </ActionIcon>
                            </Tooltip>
                        }
                        tableOptions={{
                            enableSorting: true,
                            enableBottomToolbar: false,
                            enablePagination: false,
                            enableStickyHeader: false,
                            enableTopToolbar: false,
                            autoResetPageIndex: false,
                            renderRowActions: ({ row: { original, index } }) => (
                                <EditDeleteButtons
                                    key={index}
                                    handleEdit={() => openEditDialog(original)}
                                    handleDelete={() => {
                                        deleteResourceDialogFunction.deleteResource(
                                            original.id,
                                            undefined,
                                            ResourceType.V_CYCLE_ELEMENT,
                                            deleteVCycleElement
                                        );
                                    }}
                                />
                            ),
                        }}
                    />
                </div>
            )}
        </QueryBaseComponent>
    );
};
